'use client';

import { useCallback } from 'react';
import styles from './pageBanners.module.scss';

/**
 * Arrow button that scrolls the window to the bottom of the parent element when clicked.
 * 
 * @returns The scroll button component.
 * 
 * @example
 * ```tsx
 * <BannerScroll />
 * ```
 */
const BannerScroll = () => {

    /**
     * Handles the scroll event when the user clicks on the scroll button.
     * It finds the closest parent element with the class name specified in styles.container,
     * calculates the bottom position of that element, and scrolls the window to that position smoothly.
     *
     * @param event - The mouse event triggered by clicking the scroll button.
     */
    const handleScroll = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        const banner = event.currentTarget.closest(`.${styles.container}`);
        if (banner instanceof HTMLElement) {
            const bannerBottom = banner.offsetTop + banner.offsetHeight;
            window.scrollTo({
                top: bannerBottom,
                left: 0,
                behavior: 'smooth',
            });
        }
    }, []);

    return (
        <div className={styles.skip} onClick={handleScroll}>
            {/* SVG of an arrow pointing down */}
            <svg
                width="24"
                height="24"
                xmlns="http://www.w3.org/2000/svg"
                fillRule="evenodd"
                clipRule="evenodd"
            >
                <defs>
                    {/* Animated Gradient for the arrow */}
                    <linearGradient
                        id="skip-gradient"
                        x1="50%"
                        y1="0%"
                        x2="50%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#c1b6a7">
                            <animate
                                attributeName="stop-color"
                                values="#c1b6a7; #F2EFEC; #c1b6a7"
                                dur="10s"
                                repeatCount="indefinite"
                            ></animate>
                        </stop>
                        <stop offset="100%" stopColor="#F2EFEC">
                            <animate
                                attributeName="stop-color"
                                values="#F2EFEC; #c1b6a7; #F2EFEC"
                                dur="10s"
                                repeatCount="indefinite"
                            ></animate>
                        </stop>
                    </linearGradient>
                </defs>
                {/* Arrow path */}
                <path
                    fill="url('#skip-gradient')"
                    d="M 12 0 z z m 5.247 8 l -5.247 6.44 l -5.263 -6.44 l -0.737 0.678 l 6 7.322 l 6 -7.335 l -0.753 -0.665 z"
                />
            </svg>
        </div>
    );
};

export default BannerScroll;
